import React, { useState } from 'react';
import { FileUp, Plus, Trash2, Users } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { AttendeePlaceholderRow } from './AttendeePlaceholderRow';
import { AttendeesImportModal } from './AttendeesImportModal';
import { PracticeDraft } from '../../../types/practice';
import { parseAmount } from '../../../utils/currency';
import { newId, placeholderLabel, withAttendees, withTotal } from '../../../utils/practices';
interface PracticeAttendeesProps {
  draft: PracticeDraft;
  onChange: (changes: Partial<PracticeDraft>) => void;
  onOpenPicker: () => void;
}
const emptyRecord = {
  firstName: '',
  lastName: '',
  email: ''
};
export function PracticeAttendees({
  draft,
  onChange,
  onOpenPicker
}: PracticeAttendeesProps) {
  const attendees = draft.attendees || [];
  const isOccasional = draft.clientType === 'occasionale';
  const real = attendees.filter(a => !a.placeholder);
  const slots = attendees.filter(a => a.placeholder);
  const [record, setRecord] = useState(emptyRecord);
  const [recordError, setRecordError] = useState('');
  const [isImportOpen, setIsImportOpen] = useState(false);
  const addRecord = () => {
    if (!record.firstName.trim() || !record.lastName.trim() || !record.email.trim()) {
      setRecordError('Inserisci nome, cognome ed email personale');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(record.email.trim())) {
      setRecordError("Inserisci un'email valida");
      return;
    }
    onChange(withAttendees(draft, [...real, {
      id: newId('att'),
      firstName: record.firstName.trim(),
      lastName: record.lastName.trim(),
      email: record.email.trim()
    }, ...slots]));
    setRecord(emptyRecord);
    setRecordError('');
  };

  // A removed person leaves an anonymous slot, so the total is kept
  const removeReal = (id: string) => onChange(withAttendees(draft, attendees.filter(a => a.id !== id)));

  // A removed anonymous slot lowers the total
  const removeSlot = (id: string) => onChange({
    attendees: attendees.filter(a => a.id !== id),
    participantsCount: Math.max(0, (draft.participantsCount || 1) - 1) || undefined
  });
  const completeSlot = (id: string, data: {
    firstName: string;
    lastName: string;
    email: string;
  }) => onChange({
    attendees: attendees.map(a => a.id === id ? {
      ...a,
      ...data,
      placeholder: false
    } : a)
  });
  const commitTotal = (value: string) => {
    const n = parseAmount(value);
    onChange(withTotal(draft, n === undefined ? undefined : Math.floor(n)));
  };
  return <div className="space-y-4" data-id="element-1484" data-mp="aj3t88vvwi82">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3" data-id="element-1485" data-mp="aiau9u362x0h">
        <div className="sm:w-72" data-id="element-1486" data-mp="2q9owxpq3rw7s">
          <Input key={`total-${draft.participantsCount ?? ''}`} label="Numero complessivo partecipanti" type="number" min={real.length || 1} step={1} defaultValue={draft.participantsCount ?? ''} onBlur={e => commitTotal(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitTotal(e.currentTarget.value);
          }
        }} description="Se mancano persone, vengono creati partecipanti anonimi da completare" data-id="element-1487" data-mp="2q8na6nzq5la8" />
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1488" data-mp="23zgiuwjvtt4f">
          {!isOccasional && <Button variant="flat" color="secondary" size="sm" startContent={<Users className="h-4 w-4" data-id="element-1490" data-mp="2dtgy3r8voeqv" />} onClick={onOpenPicker} data-id="element-1489" data-mp="2022b79buntej">
              {real.some(a => a.workerId !== undefined) ? 'Modifica da anagrafica' : 'Seleziona da anagrafica'}
            </Button>}
          <Button variant="flat" color="secondary" size="sm" startContent={<FileUp className="h-4 w-4" data-id="element-1492" data-mp="1twuomrm7poc3" />} onClick={() => setIsImportOpen(true)} data-id="element-1491" data-mp="1gbp4lnes760i">
            Importa da file
          </Button>
        </div>
      </div>

      <div className="rounded-xl bg-slate-50 p-4" data-id="element-1493" data-mp="23wd9da9y5e57">
        <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1494" data-mp="28nlgfznt0nf7">
          {isOccasional ? 'Aggiungi partecipante' : 'Aggiungi altro partecipante'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.3fr_auto] gap-3 items-end" data-id="element-1495" data-mp="2cp70c4yra1yw">
          <Input label="Nome" value={record.firstName} onChange={e => setRecord({
          ...record,
          firstName: e.target.value
        })} data-id="element-1496" data-mp="2hmys8yzeho33" />
          <Input label="Cognome" value={record.lastName} onChange={e => setRecord({
          ...record,
          lastName: e.target.value
        })} data-id="element-1497" data-mp="2rlobav319ekx" />
          <Input label="Email personale" type="email" value={record.email} onChange={e => setRecord({
          ...record,
          email: e.target.value
        })} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addRecord();
          }
        }} data-id="element-1498" data-mp="x8ewqovmr2vt" />
          <Button startContent={<Plus className="h-4 w-4" data-id="element-1500" data-mp="2wrjq5mtfs7z8" />} onClick={addRecord} data-id="element-1499" data-mp="31ojvtwmdj8pt">
            Aggiungi
          </Button>
        </div>
        {recordError ? <p className="text-xs text-red-600 font-medium mt-2" data-id="element-1501" data-mp="3tgsqcuymlqzr">{recordError}</p> : <p className="text-xs text-slate-500 mt-2" data-id="element-1502" data-mp="3pha5fz6t1fwx">
            {isOccasional ? "Le persone restano salvate solo in questa pratica: l'azienda occasionale non è in anagrafica clienti." : 'Persone non presenti in anagrafica: restano salvate solo in questa pratica.'}
          </p>}
      </div>

      <div data-id="element-1503" data-mp="293a72snlxybs">
        <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-1504" data-mp="28zw53liuvr4v">
          Partecipanti al corso
          <span className="font-normal text-slate-500" data-id="element-1505" data-mp="244yzkoksbzwq">
            {' '}
            · {attendees.length}
            {slots.length > 0 && ` (${slots.length} da completare)`}
          </span>
        </p>
        {attendees.length === 0 ? <p className="text-sm text-slate-500" data-id="element-1506" data-mp="1wjwgt8aa72vz">Nessun partecipante indicato.</p> : <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-[28rem] overflow-y-auto" data-id="element-1507" data-mp="21hy98jtlkek4">
            {real.map(a => <li key={a.id} className="flex items-center gap-3 px-4 py-2" data-id="element-1508" data-mp="1ms8v7o0eog2f">
                <span className="flex-1 min-w-0" data-id="element-1509" data-mp="1rl1xm58ejcbc">
                  <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-1510" data-mp="1dsvlcu8ddph6">
                    {a.firstName} {a.lastName}
                  </span>
                  <span className="block text-xs text-slate-500 truncate" data-id="element-1511" data-mp="1hv07z90ehlub">
                    {a.email}
                    {a.workerId !== undefined && ' · da anagrafica'}
                  </span>
                </span>
                <button type="button" onClick={() => removeReal(a.id)} aria-label={`Rimuovi ${a.firstName} ${a.lastName}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-1512" data-mp="18vvfokffmoql">
                  <Trash2 className="h-4 w-4" data-id="element-1513" data-mp="u73ev2ekuw06" />
                </button>
              </li>)}
            {slots.map((a, i) => <AttendeePlaceholderRow key={a.id} label={placeholderLabel(real.length + i)} onComplete={data => completeSlot(a.id, data)} onRemove={() => removeSlot(a.id)} data-id="element-1514" data-mp="185tcqk980zpl" data-mpi={i} />)}
          </ul>}
      </div>

      <AttendeesImportModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} onConfirm={list => {
      onChange(withAttendees(draft, [...real, ...list, ...slots]));
      setIsImportOpen(false);
    }} data-id="element-1515" data-mp="1cyminbgfee39" />
    </div>;
}